import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { minimalClassTimeSchema } from './class-time-shared.js';
import { meetingBlockLikeSchema } from './meeting-block.js';
import { DEPRECATED_pagingSchemaWithMaxPageSize } from './modifier.js';
import { scheduleBreakLikeSchema, scheduleBreakSchema } from './schedule-break.js';
import { schoolWithOrgSchema } from './school-shared.js';

export const { ScheduleStatus } = PrismaEnums;
export type ScheduleStatus = PrismaEnums.ScheduleStatus;
export const scheduleStatusSchema = z.nativeEnum(ScheduleStatus);

export const scheduleLikeSchema = z.object({
  timeZone: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  practicePlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  diagnosticPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  classTimes: z.array(minimalClassTimeSchema),
  scheduleBreaks: z.array(scheduleBreakLikeSchema),
});
export type ScheduleLike = z.infer<typeof scheduleLikeSchema>;

/**
 * Schedules come in different flavors. They are ordered from least amount of
 * information to most. Some of them correspond to visible fields that certain
 * roles are allowed to see.
 */

export const plainScheduleSchema = () =>
  z.object({
    id: z.string(),
    name: z.string(),
    timeZone: z.string(),
    status: scheduleStatusSchema,
    practicePlainDate: z.instanceof(Temporal.PlainDate).nullable(),
    diagnosticPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
    schoolId: z.string(),
    startPlainDate: z.instanceof(Temporal.PlainDate),
    endPlainDate: z.instanceof(Temporal.PlainDate),
  });
/**
 * A schedule with no joins.
 */
export type PlainSchedule = z.infer<ReturnType<typeof plainScheduleSchema>>;

export const minimalScheduleSchema = plainScheduleSchema().extend({
  classTimes: z.array(minimalClassTimeSchema),
  scheduleBreaks: z.array(scheduleBreakSchema),
});
/**
 * A schedule with enough information to generate sessions from it.
 */
export type MinimalSchedule = z.infer<typeof minimalScheduleSchema>;

export const scheduleSchema = minimalScheduleSchema.extend({
  school: schoolWithOrgSchema,
});

/**
 * A full schedule with whatever information an admin needs.
 */
export type Schedule = z.infer<typeof scheduleSchema>;

export const getSchedulesOptionsSchema = DEPRECATED_pagingSchemaWithMaxPageSize.extend({
  schoolId: z.string().optional(),
  query: z.string().optional(),
  statuses: z.string().optional(),
});
export type GetSchedulesOptions = z.infer<typeof getSchedulesOptionsSchema>;

export const getSchedulesResultSchema = z.object({
  schedules: z.array(scheduleSchema),
  total: z.number(),
  pageIndex: z.number(),
  pageSize: z.number(),
});
export type GetSchedulesResult = z.infer<typeof getSchedulesResultSchema>;

export const createScheduleOptionsSchema = z.object({
  schoolId: z.string(),
  name: z.string(),
  timeZone: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  practicePlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  diagnosticPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  scheduleBreaks: z.array(scheduleBreakLikeSchema),
  classTimes: z.array(
    z.object({
      meetingBlocks: meetingBlockLikeSchema.array(),
      type: z.nativeEnum(PrismaEnums.ClassTimeType),
    }),
  ),
});
export type CreateScheduleOptions = z.infer<typeof createScheduleOptionsSchema>;

export const updateScheduleOptionsSchema = z.object({
  schoolId: z.string(),
  name: z.string(),
  timeZone: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  practicePlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  diagnosticPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  scheduleBreaks: z.array(scheduleBreakLikeSchema),
});
export type UpdateScheduleOptions = z.infer<typeof updateScheduleOptionsSchema>;

export const updateSchedulesToStatusOptionsSchema = z.object({
  status: scheduleStatusSchema,
});
export type UpdateSchedulesToStatusOptions = z.infer<typeof updateSchedulesToStatusOptionsSchema>;

export const archiveOrSoftDeleteScheduleBody = z.object({
  status: z.enum([scheduleStatusSchema.enum.archived, scheduleStatusSchema.enum.deleted]),
});

export type ArchiveOrSoftDeleteScheduleBody = z.infer<typeof archiveOrSoftDeleteScheduleBody>;
